import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useUser } from "@/lib/user";
import { api } from "@/lib/api";
import { TypingDots } from "@/components/TypingDots";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2,
  AlertCircle,
  Loader2,
  PartyPopper,
  RefreshCw,
  ArrowRight,
  BookOpen,
  Users,
  Download,
  ExternalLink,
} from "lucide-react";

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 10;
const BOOK_PDF_URL = "/founder-mode-book.pdf";
const COMMUNITY_URL =
  "https://chat.whatsapp.com/C7U7EsUvG5W5RmxaNLDXkJ?s=cl&p=a&ilr=4";

// Read a query param from either window.location.search OR the hash fragment
// (Stripe/Razorpay redirect to `/#/payment-success?...`).
function readParam(name) {
  const direct = new URLSearchParams(window.location.search).get(name);
  if (direct) return direct;
  const hash = window.location.hash || "";
  const q = hash.indexOf("?");
  if (q < 0) return null;
  return new URLSearchParams(hash.slice(q + 1)).get(name);
}

export default function PaymentSuccessPage() {
  const [, navigate] = useLocation();
  const { user, setUser } = useUser();
  const [status, setStatus] = useState("polling"); // polling | success | failed
  const [attempts, setAttempts] = useState(0);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }

    const provider = readParam("provider");
    const sessionId = readParam("session_id");

    // Razorpay: handler already verified server-side. Just refresh the user.
    if (provider === "razorpay") {
      let cancelled = false;
      (async () => {
        try {
          const fresh = await api.getUser(user.id);
          if (cancelled) return;
          setUser(fresh);
          setStatus(fresh.is_paid ? "success" : "failed");
        } catch (_) {
          if (!cancelled) setStatus("failed");
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    // Stripe: poll verify until paid or timeout.
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    let cancelled = false;
    let n = 0;

    const tick = async () => {
      if (cancelled) return;
      n += 1;
      setAttempts(n);
      try {
        const r = await api.verifyPayment({
          user_id: user.id,
          provider: "stripe",
          session_id: sessionId,
        });
        if (cancelled) return;
        if (r.success) {
          try {
            const fresh = await api.getUser(user.id);
            if (!cancelled) setUser(fresh);
          } catch (_) {
            // non-blocking — verify already succeeded
          }
          if (!cancelled) setStatus("success");
          return;
        }
      } catch (_) {
        // swallow; verify can still resolve on a later poll
      }
      if (cancelled) return;
      if (n >= MAX_POLL_ATTEMPTS) {
        setStatus("failed");
        return;
      }
      setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();

    return () => {
      cancelled = true;
    };
  }, [user, navigate, setUser, retryToken]);

  if (!user) return null;

  return (
    <main
      data-testid="payment-success-page"
      data-status={status}
      className="min-h-screen flex items-center justify-center px-6 py-16"
    >
      <div className="max-w-3xl w-full text-center">
        {status === "polling" && (
          <div data-testid="payment-polling">
            <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full border border-amber-500/30 bg-amber-500/[0.06] text-amber-300">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
            <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-amber-300">
              confirming payment
            </p>
            <h1 className="mt-3 text-2xl md:text-3xl font-semibold text-zinc-100">
              Holding the line…
            </h1>
            <p className="mt-3 text-zinc-500 text-sm">
              Verifying your transaction with Stripe (attempt {attempts}/{MAX_POLL_ATTEMPTS}).
            </p>
            <div className="mt-5 flex justify-center">
              <TypingDots />
            </div>
          </div>
        )}

        {status === "success" && (
          <div data-testid="payment-success-state">
            {/* Hero */}
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-emerald-500/40 bg-emerald-500/[0.10] text-emerald-300">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-emerald-300">
              payment confirmed
            </p>
            <h1 className="mt-3 text-2xl md:text-4xl font-semibold text-zinc-50 inline-flex items-center gap-2 flex-wrap justify-center">
              <PartyPopper className="h-6 w-6 text-amber-300" />
              Welcome to Founder Mode, {user.name?.split(" ")[0] || "Founder"}.
            </h1>
            <p className="mt-3 text-zinc-400">
              <span className="font-mono text-amber-300">11 quests</span> just unlocked.
            </p>

            {/* Two action cards */}
            <div className="mt-10 grid sm:grid-cols-2 gap-4 text-left">
              <a
                data-testid="download-book-card"
                href={BOOK_PDF_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-2xl border border-amber-500/25 bg-amber-500/[0.04] p-6 hover:border-amber-400/60 hover:bg-amber-500/[0.08] transition-colors"
              >
                <div className="flex items-center gap-3 text-amber-300">
                  <BookOpen className="h-5 w-5" />
                  <span className="font-mono text-[11px] uppercase tracking-wider">
                    the book
                  </span>
                </div>
                <h2 className="mt-3 text-lg font-semibold text-zinc-100">
                  Download The Founder Mode book
                </h2>
                <p className="mt-1 text-sm text-zinc-500">
                  PDF · 35 chapters · yours forever.
                </p>
                <div
                  data-testid="download-book-btn"
                  className="mt-5 inline-flex items-center gap-2 rounded-md bg-amber-500 group-hover:bg-amber-400 text-zinc-950 font-semibold h-10 px-4 text-sm"
                >
                  <Download className="h-4 w-4" /> Download PDF
                </div>
              </a>

              <a
                data-testid="join-community-card"
                href={COMMUNITY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-2xl border border-amber-500/25 bg-amber-500/[0.04] p-6 hover:border-amber-400/60 hover:bg-amber-500/[0.08] transition-colors"
              >
                <div className="flex items-center gap-3 text-amber-300">
                  <Users className="h-5 w-5" />
                  <span className="font-mono text-[11px] uppercase tracking-wider">
                    community
                  </span>
                </div>
                <h2 className="mt-3 text-lg font-semibold text-zinc-100">
                  Join the 1000 Founders Community
                </h2>
                <p className="mt-1 text-sm text-zinc-500">
                  599+ founders · Private WhatsApp group.
                </p>
                <div
                  data-testid="join-community-btn"
                  className="mt-5 inline-flex items-center gap-2 rounded-md bg-amber-500 group-hover:bg-amber-400 text-zinc-950 font-semibold h-10 px-4 text-sm"
                >
                  <ExternalLink className="h-4 w-4" /> Open WhatsApp invite
                </div>
              </a>
            </div>

            {/* Demoted secondary link */}
            <button
              data-testid="goto-dashboard-btn"
              onClick={() => navigate("/dashboard")}
              className="mt-10 inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-amber-300 transition-colors"
            >
              Continue to your quest map <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {status === "failed" && (
          <div data-testid="payment-failed-state">
            <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full border border-red-500/40 bg-red-500/[0.06] text-red-300">
              <AlertCircle className="h-6 w-6" />
            </div>
            <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-red-300">
              not confirmed yet
            </p>
            <h1 className="mt-3 text-2xl md:text-3xl font-semibold text-zinc-100">
              Payment not confirmed yet.
            </h1>
            <p className="mt-3 text-zinc-500 text-sm">
              If you completed the purchase, refresh in a minute. Otherwise try again or contact
              support.
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              <Button
                data-testid="payment-retry-btn"
                onClick={() => {
                  setAttempts(0);
                  setStatus("polling");
                  setRetryToken((t) => t + 1);
                }}
                variant="outline"
                className="h-11 border-amber-500/30 bg-transparent text-amber-300 hover:bg-amber-500/10 hover:text-amber-200 gap-2"
              >
                <RefreshCw className="h-4 w-4" /> Retry verify
              </Button>
              <Button
                data-testid="payment-failed-back-btn"
                onClick={() => navigate("/paywall")}
                className="h-11 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold"
              >
                Back to paywall
              </Button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
